import { useQueryClient } from '@tanstack/react-query';
const STORAGE_KEY = 'varos_suggestions';
const MAX_SUGGESTIONS = 25;
// Helper functions for sessionStorage operations
function getSuggestionsFromStorage() {
    try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    }
    catch (error) {
        console.error('Error reading suggestions from sessionStorage:', error);
        return [];
    }
}
function saveSuggestionsToStorage(suggestions) {
    try {
        // Enforce maximum number of suggestions by keeping only the most recent ones
        let limitedSuggestions = suggestions;
        if (suggestions.length > MAX_SUGGESTIONS) {
            // Sort by created_at descending (newest first) and take the first MAX_SUGGESTIONS
            limitedSuggestions = suggestions
                .sort((a, b) => new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime())
                .slice(0, MAX_SUGGESTIONS);
        }
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(limitedSuggestions));
    }
    catch (error) {
        console.error('Error saving suggestions to sessionStorage:', error);
    }
}
function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
}
export function createSuggestionRepository(config) {
    const { repository } = config;
    function getSuggestionListQueryKey(props) {
        return ['research', 'suggestion', 'find', props];
    }
    function getSuggestionLookupQueryKey(props) {
        return ['research', 'suggestion', 'lookup', props.id];
    }
    return {
        useFind(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getSuggestionListQueryKey(props),
                async queryFn() {
                    const allSuggestions = getSuggestionsFromStorage();
                    // Filter suggestions by content if specified
                    let filteredSuggestions = allSuggestions;
                    if (props.content) {
                        filteredSuggestions = filteredSuggestions.filter((s) => s.content === props.content);
                    }
                    if (props.id) {
                        filteredSuggestions = filteredSuggestions.filter((s) => s.id === props.id);
                    }
                    return filteredSuggestions;
                },
            });
            return query;
        },
        useLookup(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getSuggestionLookupQueryKey(props),
                async queryFn() {
                    const allSuggestions = getSuggestionsFromStorage();
                    const suggestion = allSuggestions.find((s) => s.id === props.id);
                    if (!suggestion) {
                        throw new Error(`Suggestion with id ${props.id} not found`);
                    }
                    return suggestion;
                },
            });
            return query;
        },
        useCreate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const allSuggestions = getSuggestionsFromStorage();
                    // Each content can have at most one active suggestion
                    // Remove any existing suggestion for this content before creating a new one
                    const filteredSuggestions = allSuggestions.filter((suggestion) => suggestion.content !== props.content);
                    const newSuggestion = {
                        id: generateId(),
                        object: 'suggestion',
                        status: 'pending',
                        creator: null,
                        content: props.content,
                        operations: props.operations.map((operation) => ({
                            ...operation,
                            status: 'pending',
                        })),
                        created_at: new Date(),
                        updated_at: new Date(),
                    };
                    const updatedSuggestions = [...filteredSuggestions, newSuggestion];
                    saveSuggestionsToStorage(updatedSuggestions);
                    return newSuggestion;
                },
                async onSuccess(data) {
                    // Invalidate all suggestion queries since we might have replaced an existing suggestion
                    await queryClient.refetchQueries({
                        queryKey: ['research', 'suggestion'],
                    });
                },
            });
        },
        useUpdate(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const allSuggestions = getSuggestionsFromStorage();
                    const suggestionIndex = allSuggestions.findIndex((s) => s.id === props.id);
                    if (suggestionIndex === -1) {
                        throw new Error(`Suggestion with id ${props.id} not found`);
                    }
                    const existingSuggestion = allSuggestions[suggestionIndex];
                    // Update operations if provided
                    const updatedOperations = props.operations ?? existingSuggestion.operations;
                    // Determine overall suggestion status based on operation statuses
                    let overallStatus = props.status ?? existingSuggestion.status;
                    // If operations are being updated, calculate the overall status
                    if (updatedOperations && updatedOperations.length > 0) {
                        const operationStatuses = updatedOperations.map((op) => op.status);
                        const acceptedCount = operationStatuses.filter((status) => status === 'accepted').length;
                        const rejectedCount = operationStatuses.filter((status) => status === 'rejected').length;
                        const totalCount = operationStatuses.length;
                        if (acceptedCount === totalCount) {
                            // All operations accepted
                            overallStatus = 'accepted';
                        }
                        else if (rejectedCount === totalCount) {
                            // All operations rejected
                            overallStatus = 'rejected';
                        }
                        else if (acceptedCount > 0 || rejectedCount > 0) {
                            // Mix of accepted/rejected operations
                            overallStatus = 'partial';
                        }
                        // If all operations are still pending, keep existing status
                    }
                    const updatedSuggestion = {
                        id: existingSuggestion.id,
                        object: existingSuggestion.object,
                        content: existingSuggestion.content,
                        creator: existingSuggestion.creator,
                        created_at: existingSuggestion.created_at,
                        updated_at: new Date(),
                        status: overallStatus,
                        operations: updatedOperations,
                    };
                    const updatedSuggestions = [...allSuggestions];
                    updatedSuggestions[suggestionIndex] = updatedSuggestion;
                    saveSuggestionsToStorage(updatedSuggestions);
                    return updatedSuggestion;
                },
                async onSuccess(data) {
                    await queryClient.refetchQueries({
                        queryKey: ['research', 'suggestion'],
                    });
                },
            });
        },
        useAccept(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const allSuggestions = getSuggestionsFromStorage();
                    const suggestionIndex = allSuggestions.findIndex((s) => s.id === props.id);
                    if (suggestionIndex === -1) {
                        throw new Error(`Suggestion with id ${props.id} not found`);
                    }
                    const existingSuggestion = allSuggestions[suggestionIndex];
                    // Update only pending operations to accepted status
                    const updatedOperations = existingSuggestion.operations.map((op) => ({
                        ...op,
                        status: op.status === 'pending' ? 'accepted' : op.status,
                    }));
                    // Calculate overall status based on all operation statuses
                    const operationStatuses = updatedOperations.map((op) => op.status);
                    const acceptedCount = operationStatuses.filter((status) => status === 'accepted').length;
                    const rejectedCount = operationStatuses.filter((status) => status === 'rejected').length;
                    const totalCount = operationStatuses.length;
                    let overallStatus;
                    if (acceptedCount === totalCount) {
                        overallStatus = 'accepted';
                    }
                    else if (rejectedCount === totalCount) {
                        overallStatus = 'rejected';
                    }
                    else if (acceptedCount > 0 || rejectedCount > 0) {
                        overallStatus = 'partial';
                    }
                    else {
                        overallStatus = 'pending';
                    }
                    const updatedSuggestion = {
                        ...existingSuggestion,
                        status: overallStatus,
                        operations: updatedOperations,
                        updated_at: new Date(),
                    };
                    const updatedSuggestions = [...allSuggestions];
                    updatedSuggestions[suggestionIndex] = updatedSuggestion;
                    saveSuggestionsToStorage(updatedSuggestions);
                    return updatedSuggestion;
                },
                async onSuccess(data) {
                    await queryClient.refetchQueries({
                        queryKey: ['research', 'suggestion'],
                    });
                },
            });
        },
        useReject(context) {
            const queryClient = useQueryClient();
            return repository.useMutation(context, {
                async mutationFn(props) {
                    const allSuggestions = getSuggestionsFromStorage();
                    const suggestionIndex = allSuggestions.findIndex((s) => s.id === props.id);
                    if (suggestionIndex === -1) {
                        throw new Error(`Suggestion with id ${props.id} not found`);
                    }
                    const existingSuggestion = allSuggestions[suggestionIndex];
                    // Update only pending operations to rejected status
                    const updatedOperations = existingSuggestion.operations.map((op) => ({
                        ...op,
                        status: op.status === 'pending' ? 'rejected' : op.status,
                    }));
                    // Calculate overall status based on all operation statuses
                    const operationStatuses = updatedOperations.map((op) => op.status);
                    const acceptedCount = operationStatuses.filter((status) => status === 'accepted').length;
                    const rejectedCount = operationStatuses.filter((status) => status === 'rejected').length;
                    const totalCount = operationStatuses.length;
                    let overallStatus;
                    if (acceptedCount === totalCount) {
                        overallStatus = 'accepted';
                    }
                    else if (rejectedCount === totalCount) {
                        overallStatus = 'rejected';
                    }
                    else if (acceptedCount > 0 || rejectedCount > 0) {
                        overallStatus = 'partial';
                    }
                    else {
                        overallStatus = 'pending';
                    }
                    const updatedSuggestion = {
                        ...existingSuggestion,
                        status: overallStatus,
                        operations: updatedOperations,
                        updated_at: new Date(),
                    };
                    const updatedSuggestions = [...allSuggestions];
                    updatedSuggestions[suggestionIndex] = updatedSuggestion;
                    saveSuggestionsToStorage(updatedSuggestions);
                    return updatedSuggestion;
                },
                async onSuccess(data) {
                    await queryClient.refetchQueries({
                        queryKey: ['research', 'suggestion'],
                    });
                },
            });
        },
    };
}
//# sourceMappingURL=suggestionRepository.js.map